import { useData, useDataAuth } from "../../../runtime/provider";
import {
  useInfiniteQuery,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { trpc } from "@utils/trpc";
import {
  recentSearchesPaginatedQuery,
  recentSearchesPaginatedQueryKey,
  recentSearchesQuery,
  recentSearchesQueryKey,
} from "./queryFunctions";

/**
 * Get the list of recent searches, add and remove recent searches. Used
 * for the search screen.
 */
export const useSearch = (limit?: number) => {
  const data = useData();
  const queryClient = useQueryClient();
  const { firebaseUser } = useDataAuth();
  const queryEnabled = !!firebaseUser;
  const queryKey = recentSearchesQueryKey(limit);

  const query = useQuery({
    ...recentSearchesQuery(data, limit),
    enabled: queryEnabled,
  });

  const addSearch = trpc.user.addRecentSearch.useMutation({
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey,
      });
    },
  });

  const removeSearch = trpc.user.removeRecentSearch.useMutation({
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey,
      });
    },
  });

  return { query, addSearch, removeSearch };
};

export const useSearchMutations = () => {
  const queryClient = useQueryClient();

  const invalidateRecentSearches = async () => {
    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: recentSearchesQueryKey(),
      }),
      queryClient.invalidateQueries({
        queryKey: recentSearchesPaginatedQueryKey(20),
      }),
    ]);
  };

  const addSearch = trpc.user.addRecentSearch.useMutation({
    onSuccess: invalidateRecentSearches,
  });

  const removeSearch = trpc.user.removeRecentSearch.useMutation({
    onSuccess: invalidateRecentSearches,
  });

  return { addSearch, removeSearch };
};

export const useRecentSearchesPaginated = (limit = 20) => {
  const data = useData();
  const queryClient = useQueryClient();
  const { firebaseUser } = useDataAuth();
  const queryEnabled = !!firebaseUser;
  const queryKey = recentSearchesPaginatedQueryKey(limit);

  const query = useInfiniteQuery({
    ...recentSearchesPaginatedQuery(data, limit),
    enabled: queryEnabled,
  });

  const addSearch = trpc.user.addRecentSearch.useMutation({
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey });
    },
  });

  const removeSearch = trpc.user.removeRecentSearch.useMutation({
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey });
    },
  });

  return { query, addSearch, removeSearch };
};
